// Hero, Problem components

import { useState, useEffect, useRef } from 'react';

/* ---------- TopBar ---------- */
function TopBar({ theme, onToggleTheme }) {
  const isDark = theme !== 'light';
  const nextTheme = isDark ? 'light' : 'dark';
  return (
    <header className="topbar">
      <div className="topbar-inner">
        <div className="brandmark"><span className="zero">02100</span></div>
        <div className="status">
          <span className="hide-sm">Zero to One Hundred</span>
        </div>
        <nav>
          <a href="#problem">problem</a>
          <a href="#trials">trials</a>
          <a href="#verdict">verdict</a>
          <a href="#pricing">pricing</a>
          <a href="#club">club</a>
          <a href="#faq">faq</a>
        </nav>
        <button
          type="button"
          className="theme-toggle"
          onClick={onToggleTheme}
          aria-label={`Switch to ${nextTheme} theme`}
          title={`Switch to ${nextTheme} theme  (⌘⇧L)`}
        >
          <span className="icon" aria-hidden="true">{isDark ? '◐' : '◑'}</span>
        </button>
        <a href="#apply" className="apply-btn">Apply →</a>
      </div>
    </header>
  );
}

/* ---------- Hero ---------- */
function Hero() {
  return (
    <section id="hero" style={{paddingTop: 40, paddingBottom: 40, borderTop:'none'}} data-screen-label="hero">
      <HeroSplit/>
    </section>
  );
}

function HeroSplit() {
  return (
    <div>
      <div style={{display:'grid', gridTemplateColumns:'1.05fr 1fr', gap: 56, alignItems:'stretch'}} className="hero-grid">
        <div style={{display:'flex', flexDirection:'column', justifyContent:'space-between'}}>
          <div>
            <div style={{fontFamily:'var(--mono)', fontSize:10, letterSpacing:4, color:'var(--signal)', fontWeight:700, textTransform:'uppercase', marginBottom:20, display:'flex', alignItems:'center', gap:12}}>
              <span style={{display:'inline-block', width:8, height:8, background:'var(--signal)'}}/>
              <span>02100.club · cohort 02 · next intake</span>
            </div>
            <h1>Prove it<br/><em>before you build it.</em></h1>
            <p className="large" style={{marginTop:24, maxWidth:'34ch'}}>
              The venture club for founders who have been burned once and will not be burned twice. Evidence over valuation, receipts over decks.
            </p>
          </div>
          <div style={{display:'flex', gap:12, marginTop:40, flexWrap:'wrap'}}>
            <a href="#apply" className="btn primary">Apply to 02100 <span className="arrow">→</span></a>
            <a href="#trials" className="btn">See the trials</a>
          </div>
        </div>

        <div className="card" style={{background:'var(--bg)', border:'1px solid var(--signal)', padding:0, overflow:'hidden', position:'relative', display:'flex', flexDirection:'column'}}>
          <div style={{padding:'14px 20px', borderBottom:'1px solid var(--signal)', color:'var(--signal)', fontSize:10, letterSpacing:3, textTransform:'uppercase', fontWeight:700, display:'flex', justifyContent:'space-between', alignItems:'center'}}>
            <span>§ Case file · MBR-019</span><span style={{color:'var(--fg-dim)', letterSpacing:2}}>illustrative · 2026.Q1</span>
          </div>

          <div style={{padding:'28px 28px 20px', borderBottom:'1px dashed var(--border)', position:'relative'}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:16}}>
              <div>
                <p style={{color:'var(--fg-dim)', fontSize:10, letterSpacing:3, textTransform:'uppercase', margin:0, fontWeight:700}}>Previous attempt · pre-02100</p>
                <p style={{fontSize:18, color:'var(--fg-bright)', marginTop:10, marginBottom:0, fontFamily:'var(--display)', fontStyle:'var(--display-style)', lineHeight:1.25}}>
                  14 months. €380K burned.<br/>Zero paying users.
                </p>
              </div>
              <span className="stamp" style={{color:'var(--verdict-fail)', transform:'rotate(-4deg)', flexShrink:0, fontSize:9}}>FAIL</span>
            </div>
          </div>

          <div style={{padding:'24px 28px 28px', background:'var(--surface)', flex:1}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:16}}>
              <div>
                <p style={{color:'var(--signal)', fontSize:10, letterSpacing:3, textTransform:'uppercase', margin:0, fontWeight:700}}>After 02100 · T1 through T3</p>
                <p style={{fontSize:18, color:'var(--fg-bright)', marginTop:10, marginBottom:0, fontFamily:'var(--display)', fontStyle:'var(--display-style)', lineHeight:1.25}}>
                  6 months. €95K flat.<br/>€12K in pre-orders.
                </p>
              </div>
              <span className="stamp" style={{color:'var(--verdict-pass)', transform:'rotate(3deg)', flexShrink:0, fontSize:9}}>PASS</span>
            </div>
          </div>
        </div>
      </div>

      <div style={{marginTop: 56, display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:0, borderTop:'1px solid var(--border)', borderBottom:'1px solid var(--border)'}}>
        <Stat k="€95K" v="total · 6 months" />
        <Stat k="3" v="trials · 3 verdicts" />
        <Stat k="02/04" v="next-cohort slots open" />
      </div>
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div style={{padding:'20px 0', borderRight:'1px solid var(--border)'}}>
      <div style={{fontSize:28, color:'var(--signal)', fontWeight:800, letterSpacing:-1, fontFamily:'var(--mono)'}}>{k}</div>
      <div style={{fontSize:10, color:'var(--fg-dim)', letterSpacing:2, textTransform:'uppercase'}}>{v}</div>
    </div>
  );
}

/* ---------- Problem: animated casualty audit ---------- */
function AnimatedBar({ label, pct, tag, delay = 0, visible }) {
  const [w, setW] = useState(0);
  useEffect(() => {
    if (!visible) return;
    const id = setTimeout(() => setW(pct), delay);
    return () => clearTimeout(id);
  }, [visible, pct, delay]);
  return (
    <div style={{padding:'18px 0', borderBottom:'1px solid var(--border)'}}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:10}}>
        <span style={{fontSize:13, color:'var(--fg-bright)', fontWeight:500}}>{label}</span>
        <span style={{fontFamily:'var(--mono)', fontSize:11, color:'var(--fg-dim)', letterSpacing:1}}>{tag}</span>
      </div>
      <div style={{position:'relative', height: 10, background:'var(--surface-2)', overflow:'hidden'}}>
        <div style={{position:'absolute', inset:0, width: w + '%', background:'var(--fg-bright)', transition:'width 1.6s cubic-bezier(.2,.8,.2,1)'}}></div>
      </div>
      <div style={{fontFamily:'var(--mono)', fontSize:22, color:'var(--fg-bright)', fontWeight:800, marginTop:8, letterSpacing:-0.5}}>
        <AnimatedNumber target={pct} visible={visible} delay={delay} suffix="%" />
      </div>
    </div>
  );
}

function AnimatedNumber({ target, visible, delay = 0, suffix = '', prefix = '' }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!visible) return;
    let start;
    const duration = 1400;
    const startTimeout = setTimeout(() => {
      const step = (ts) => {
        if (!start) start = ts;
        const t = Math.min(1, (ts - start) / duration);
        const eased = 1 - Math.pow(1 - t, 3);
        setN(Math.round(target * eased));
        if (t < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }, delay);
    return () => clearTimeout(startTimeout);
  }, [visible, target, delay]);
  return <span>{prefix}{n}{suffix}</span>;
}

function useVisible(ref, threshold = 0.15) {
  const [v, setV] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) setV(true); }, { threshold });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [ref, threshold]);
  return v;
}

function CasualtyAudit() {
  const ref = useRef(null);
  const vis = useVisible(ref);
  const rows = [
    { label: 'Built before validating demand',  pct: 78, tag: '936 / 1,200' },
    { label: 'Ran out of runway pre-revenue',   pct: 63, tag: '756 / 1,200' },
    { label: 'Would do it differently',         pct: 91, tag: '1,092 / 1,200' },
    { label: 'Got honest data from advisors',   pct: 17, tag: '204 / 1,200' },
    { label: 'Pivoted too late',                pct: 54, tag: '648 / 1,200' },
  ];
  return (
    <div ref={ref} className="card" style={{padding:'32px 36px 16px', marginBottom:32}}>
      <div style={{display:'flex', justifyContent:'space-between', marginBottom:20, alignItems:'baseline', paddingBottom: 16, borderBottom:'1px solid var(--border)'}}>
        <div>
          <div style={{color:'var(--signal)', fontSize:10, letterSpacing:3, textTransform:'uppercase', fontWeight:700}}>Dataset · Founder Casualty Audit</div>
          <div style={{fontFamily:'var(--display)', fontStyle:'var(--display-style)', fontSize: 32, color:'var(--fg-bright)', marginTop:4, lineHeight:1.1}}>Most founders are the data.</div>
        </div>
        <div style={{fontSize:10, color:'var(--fg-dim)', textAlign:'right', letterSpacing:1}}>n = 1,200<br/>Mattermark Founder Attrition Study, 2024</div>
      </div>
      {rows.map((r, i) => <AnimatedBar key={r.label} {...r} visible={vis} delay={i * 180} />)}
    </div>
  );
}

function ProblemSection() {
  return (
    <section id="problem" data-screen-label="problem">
      <div className="sec-head">
        <div className="sec-label">01 · The Pattern</div>
        <div>
          <h2>The last time<br/>you built before <em>proving,</em><br/>it cost you two years<br/>and a cap table.</h2>
          <p className="lead dim" style={{marginTop: 28, maxWidth:'52ch'}}>
            You have budget. You have scar tissue. You have seen what €400K of "just ship it" looks like when the users never came, the retention never happened, and the deck kept saying next quarter.
          </p>
        </div>
      </div>

      <CasualtyAudit/>
    </section>
  );
}

export { TopBar, Hero, ProblemSection };
